import {inject, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {Observable} from 'rxjs';
import {SystemEvent} from '../../modules/system-event/classes/system-event';
import {map} from 'rxjs/operators';
import {SystemEventType} from '../../modules/system-event/classes/system-event-type';
import {ApplicationConfig} from '../../classes/application-config';
import {GenOutageAggregationLevel} from '../../../../generated/serverModels/GenOutageAggregationLevel';
import {HeadoutEventResultItem} from './classes/headout-event-result-item';
import {GroupedSystemEvents} from '../../modules/system-event/classes/grouped-system-events';
import {SystemEventService} from '../../modules/system-event/services/system-event.service';
import {GenSystemEventAnalyticHandle} from '../../../../generated/serverModels/GenSystemEventAnalyticHandle';
import {HeadoutEventResult} from './classes/headout-event-result';

@Injectable({
    providedIn: 'root',
})
export class HeadoutService {
    private httpClient = inject(HttpClient);
    private systemEventService = inject(SystemEventService);
    private readonly baseUrl = `api/headout`;

    constructor() {}

    public getHeadoutEvents(): Observable<GroupedSystemEvents> {
        return this.systemEventService.getGroupedEvents().pipe(
            map((groupedEvents: GroupedSystemEvents) => {
                return {
                    active: groupedEvents.active.filter((systemEvent) => systemEvent.hasAnalytic(GenSystemEventAnalyticHandle.HEADOUT)),
                    inactive: groupedEvents.inactive.filter((systemEvent) => systemEvent.hasAnalytic(GenSystemEventAnalyticHandle.HEADOUT)),
                };
            })
        );
    }

    public getMostRecentEvent(): Observable<SystemEvent> {
        return this.getHeadoutEvents().pipe(
            map((headoutEvents: GroupedSystemEvents) => {
                if (headoutEvents.active.length > 0) {
                    return headoutEvents.active[headoutEvents.active.length - 1];
                }

                return headoutEvents.inactive[headoutEvents.inactive.length - 1];
            })
        );
    }

    public getHeadoutEventsByType(type: SystemEventType): Observable<GroupedSystemEvents> {
        return this.getHeadoutEvents().pipe(
            map((groupedEvents: GroupedSystemEvents) => {
                return {
                    active: groupedEvents.active.filter((systemEvent) => systemEvent.type.id === type.id),
                    inactive: groupedEvents.inactive.filter((systemEvent) => systemEvent.type.id === type.id),
                };
            })
        );
    }

    public getListResults(event: SystemEvent): Observable<HeadoutEventResult[]> {
        const url = `${this.baseUrl}/listResults/${event.id}`;
        return this.httpClient.get<any[]>(url).pipe(map((res: any[]) => res.map((result) => new HeadoutEventResult(result))));
    }

    public getEventResults(event: HeadoutEventResult): Observable<HeadoutEventResultItem[]> {
        const url = `${this.baseUrl}/eventResults/${event.anlId}`;
        return this.httpClient.get<any[]>(url).pipe(
            map((res: any[]) => {
                const eventResults = res.map((systemEvent) => new HeadoutEventResultItem(systemEvent));

                const countyGeometries = ApplicationConfig.geometries.get(GenOutageAggregationLevel.county);
                const countyMap = new Map(countyGeometries.map((county) => [county.fipsCode, county]));

                eventResults.forEach((item) => {
                    if (item.fips && countyMap.has(item.fips)) {
                        item.county = countyMap.get(item.fips);
                        item.countyName = item.county.name.split('::--::')[1];
                        item.stateName = item.county.name.split('::--::')[0];
                    }
                });
                return eventResults;
            })
        );
    }
}
